import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import { FilterItemClearButton, FiltersChip } from "app/pages/sales-intelligence/common-components/filters-panel";
import { StyledFilterItemCardItemTitle } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterItemCard/styles";
import { FilterResultContainer } from "app/pages/sales-intelligence/common-components/filters-panel/components/styles";
import { TruncatedTextWithTooltip } from "app/pages/sales-intelligence/common-components/TruncatedText";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import { crmFilterKeyPrefix } from "app/pages/sales-intelligence/domain/crm-integration/utils/crm-filter-key";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters/types/leadsSearchFilters.dto";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import { commonKeys } from "app/pages/sales-intelligence/utils/i18n/common";

import { useResolveCrmAppliedState } from "../../hooks/useResolveCrmAppliedState";

const SELECTED_CRITERIA_SECTION_AUTOMATION = "selected-criteria-section";

type Props = {
    panelValue: Partial<LeadsSearchAllFiltersTypes>;
    onPanelValueUpdate?: (next: Partial<LeadsSearchAllFiltersTypes>) => void;
    showClearButton?: boolean;
    isChipActionEnabled?: boolean;
};

const buildRemoveHandler = (
    provider: CrmIntegrationProvider,
    onPanelValueUpdate: (next: Partial<LeadsSearchAllFiltersTypes>) => void,
    panelValue: Partial<LeadsSearchAllFiltersTypes>,
) => {
    return () => {
        // Clear every CRM key (fields + inclusion) for the provider — there is no single toggle to flip off.
        const prefix = crmFilterKeyPrefix(provider);
        const next = Object.fromEntries(
            Object.entries(panelValue).filter(([key]) => !key.startsWith(prefix)),
        ) as Partial<LeadsSearchAllFiltersTypes>;

        onPanelValueUpdate(next);
    };
};

export const CrmAppliedIndicator = ({
    panelValue,
    onPanelValueUpdate,
    showClearButton = false,
    isChipActionEnabled = false,
}: Props) => {
    const { translate } = useTranslate();

    const state = useResolveCrmAppliedState(panelValue);

    if (!state) {
        return null;
    }

    const { provider, inclusionValue } = state;
    const providerName = getCrmName(provider);
    const inclusionLabel = translate(commonKeys.inclusion(inclusionValue));
    const handleRemove = onPanelValueUpdate ? buildRemoveHandler(provider, onPanelValueUpdate, panelValue) : undefined;

    return (
        <Stack
            gap="8px"
            padding="12px"
            background={colorsPalettes.bluegrey["100"]}
            borderRadius="8px"
            data-automation={SELECTED_CRITERIA_SECTION_AUTOMATION}
            data-filter-key="crm-applied"
        >
            <Flex
                width="100%"
                alignItems="center"
                gap="6px"
            >
                <StyledFilterItemCardItemTitle>
                    <TruncatedTextWithTooltip
                        fontVariant="button"
                        color={colorsPalettes.bluegrey["600"]}
                    >
                        {providerName}
                    </TruncatedTextWithTooltip>
                </StyledFilterItemCardItemTitle>
                {showClearButton && handleRemove ? <FilterItemClearButton onClick={handleRemove} /> : null}
            </Flex>
            <FilterResultContainer>
                <FiltersChip
                    text={inclusionLabel}
                    hideActionButton={!isChipActionEnabled}
                    onClose={handleRemove}
                />
            </FilterResultContainer>
        </Stack>
    );
};
